import { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios'
import { ItemCache } from '@/utilities/cache'
import {
  type CreateEligibilityQuestionnaireDTOType,
  type EligibilityQuestionnaireResponsesSchemaType,
  type KitValidationResponseSchemaType,
  type KitTrackingUrlsSchemaType,
  type PaginatedResponseSchema_KitWithResultsSchema_Type as Kits,
  type OrderAutofillResponseSchemaType,
  type PatientPrimaryCareVerificationDTOType,
} from '@/types/portal-api.generated'
import { portalApiClient } from '@/platform/http'
import { applyPortalApiBrowserRequestHeadersSync } from '@/platform/http/requestInterceptors'
import { getPortalApiBaseUrl } from '@/platform/http/portalApiBaseUrl'
import debugKits from '@/utilities/debug/debugKits'
import { batch } from '@/utilities/async/batch'
import { getSessionMemberInfo } from '@/utilities/stytch/getSessionMemberInfo'
import type { StytchB2BClient } from '@stytch/nextjs/b2b'

type GetUserKitsParams = {
  records_per_page?: number
  page?: number
  kit_ids?: string[]
  exclude?: string[]
}

type RegisterKitData = {
  kit_id: string
  patient_consent?: {
    consented_at: string
    agreement_id: number | null
    version_id: number | null
    source?: string
  } | null
  has_pcp?: boolean
  pcp?: {
    send_fax_results_to_pcp?: boolean
    pcp_name?: string | null
    pcp_fax?: string | null
    pcp_address1?: string | null
    pcp_address2?: string | null
    pcp_city?: string | null
    pcp_state?: string | null
    pcp_zip?: string | null
  } | null
}

type ValidateKitData = {
  kit_id: string
}

export type KitValidationInfo = {
  partnerId?: string
  sku?: string
}

export function extractKitPartnerId(
  response: KitValidationResponseSchemaType | Record<string, unknown>,
): string | undefined {
  const partnerId =
    typeof response.partner_id === 'string'
      ? response.partner_id
      : typeof response.partnerId === 'string'
        ? response.partnerId
        : undefined

  const trimmed = partnerId?.trim()
  return trimmed || undefined
}

export function extractKitSku(
  response: KitValidationResponseSchemaType | Record<string, unknown>,
): string | undefined {
  const sku = typeof response.sku === 'string' ? response.sku : undefined

  const trimmed = sku?.trim()
  return trimmed || undefined
}

export function extractKitValidationInfo(
  response: KitValidationResponseSchemaType | Record<string, unknown>,
): KitValidationInfo {
  return {
    partnerId: extractKitPartnerId(response),
    sku: extractKitSku(response),
  }
}

const kitsCache = new ItemCache<Promise<Kits>>({ ttl: 60_000 })

// Serialize query params so arrays are encoded as repeated keys without [] suffix
// and empty arrays are represented as a present key with no value: ?key=
// Example: kit_ids=1&kit_ids=2&exclude= (not kit_ids[]=1&kit_ids[]=2)
const serializeParamsRepeatKeys = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      if (value.length === 0) {
        // Represent empty arrays as a present key with no value: ?key=
        searchParams.append(key, '')
      } else {
        value.forEach((item) => {
          if (item === undefined || item === null) return
          searchParams.append(key, String(item))
        })
      }
    } else {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}

const getUserKits = async (
  options?: GetUserKitsParams,
  stytchClient?: StytchB2BClient<Stytch.DefaultProjectConfiguration>,
): Promise<Kits> => {
  // As of 2025/09/21, no user has more than 24 kits. Portal API allows fetching up to 100 at once.
  // If someone did a kit every month, after 8 years and 5 months they would get their 101st kit.
  // Does anyone need to look at kit results from 8 years ago?
  // Probably not, but if that happens, we can increase the records_per_page and it will automatically paginate.
  const params = Object.assign({}, { records_per_page: 100, page: 0 }, options)

  // If records_per_page > 100 we split the request into batches of 100. There is not necessarily a
  // non-zero value for `page` that will match the intended offset with a different records_per_page size.
  if (params.records_per_page > 100 && params.page > 0) {
    throw new Error('page must be 0 if records_per_page is greater than 100')
  }

  // Only use the cache if no options are provided to avoid storing a lot of redundant data in memory
  const useCache = !options || !Object.keys(options).length
  if (useCache && kitsCache.value) {
    return kitsCache.value
  }

  const promise = fetchUserKitsPaginated(params, stytchClient).catch((err) => {
    if (useCache) kitsCache.clear() // Runs async, after the in-progress promise is cached below
    throw err
  })

  if (useCache) kitsCache.value = promise
  return promise
}

async function shouldAppendDebugKits(
  stytchClient?: StytchB2BClient<Stytch.DefaultProjectConfiguration>,
): Promise<boolean> {
  if (typeof localStorage === 'undefined') return false

  const debugKitsValue = localStorage.getItem('debug-kits')
  if (debugKitsValue === 'true') return true
  if (!debugKitsValue) return false

  const { internalClientUuid, userEmail } = getSessionMemberInfo()
  let resolvedUserEmail = userEmail

  if (stytchClient && (!resolvedUserEmail || resolvedUserEmail.trim() === '')) {
    const member = await stytchClient.self.getSync()
    resolvedUserEmail = member?.email_address ?? userEmail
  }

  return Boolean(
    internalClientUuid &&
    resolvedUserEmail &&
    debugKitsValue.split(',').includes(`${internalClientUuid}:${resolvedUserEmail}`),
  )
}

function matchingDebugKits(params: GetUserKitsParams, existingKitIds: Set<string> = new Set()) {
  return debugKits.filter(
    (kit) =>
      (!params.kit_ids?.length || params.kit_ids.includes(kit.kit_id)) &&
      !existingKitIds.has(kit.kit_id),
  )
}

async function fetchUserKitsPaginated(
  params: GetUserKitsParams & { records_per_page: number; page: number },
  stytchClient?: StytchB2BClient<Stytch.DefaultProjectConfiguration>,
): Promise<Kits> {
  const pageSize = Math.min(params.records_per_page, 100)

  const { data: firstPage } = await portalApiClient.axios.get<Kits>('/v2/users/me/kits', {
    params: { ...params, records_per_page: pageSize },
    paramsSerializer: (p) => serializeParamsRepeatKeys(p as Record<string, unknown>),
  })

  // Append debug kits when enabled via localStorage flag.
  // debug-kits can have two values:
  // - a comma-separated list of internalOrgId:userEmail pairs for which to
  //   append debug kits. These pairs are set in `updateDebugKits` in
  //   `src/flows/components/AddressInformation.tsx` when the user is bypassing
  //   kit registration
  // - `true`, expected to be set manually by developers, which will append
  //   debug kits for all accounts in the environment
  if (await shouldAppendDebugKits(stytchClient)) {
    const debugKitsToAppend = matchingDebugKits(
      params,
      new Set(firstPage.items.map((kit) => kit.kit_id)),
    )
    firstPage.items = firstPage.items.concat(debugKitsToAppend)
    firstPage.total_items += debugKitsToAppend.length
  }

  const totalDesired = Math.min(params.records_per_page, firstPage.total_items)
  if (totalDesired === firstPage.items.length) {
    return firstPage
  }

  //  Assumes params.page is 0 (if it's not, we would have returned firstPage above)
  const remainingPages = Math.ceil((totalDesired - firstPage.items.length) / pageSize)
  const pagesToFetch = Array.from({ length: remainingPages }, (_, i) => params.page + i + 1)

  // Fetch remaining pages in parallel with a max concurrency of 4
  const otherPages = await batch(
    pagesToFetch,
    1,
    async ([page]) => {
      const { data } = await portalApiClient.axios.get<Kits>('/v2/users/me/kits', {
        params: { ...params, records_per_page: pageSize, page },
        paramsSerializer: (p) => serializeParamsRepeatKeys(p as Record<string, unknown>),
      })
      return data.items
    },
    4,
  )

  return {
    items: firstPage.items.concat(otherPages).slice(0, params.records_per_page),
    page_size: params.records_per_page,
    page: params.page, // Will always be 0 here. If page > 0 we would have returned firstPage above
    total_pages: Math.ceil(firstPage.total_items / params.records_per_page),
    total_items: firstPage.total_items,
  }
}

const registerKit = async (data: RegisterKitData) => {
  await portalApiClient.register_kits_v2_users_me_kits_post(data)
  kitsCache.clear()
}

const validateKit = async (data: ValidateKitData) =>
  await portalApiClient.validate_kit_v2_kits_validate_post(data)

const getKitTrackingUrls = async (
  kitId: string,
  options: { signal?: AbortSignal } = {},
): Promise<KitTrackingUrlsSchemaType> => {
  return portalApiClient.get_kit_tracking_urls_v2_users_me_kits__kit_id__tracking_get({
    params: { kit_id: kitId },
    signal: options.signal,
  })
}

export async function fetchKitValidationInfo(kitId: string): Promise<KitValidationInfo> {
  const trimmedKitId = kitId.trim()
  if (!trimmedKitId) return {}

  const result = await validateKit({ kit_id: trimmedKitId })
  return extractKitValidationInfo(result)
}

export async function fetchKitPartnerId(kitId: string): Promise<string | undefined> {
  const { partnerId } = await fetchKitValidationInfo(kitId)
  return partnerId
}

export async function get_primary_care_provider_from_order(
  body: PatientPrimaryCareVerificationDTOType,
  options: { kitId: string; signal?: AbortSignal },
): Promise<OrderAutofillResponseSchemaType> {
  return portalApiClient.get_order_primary_care_provider_v2_kits__kit_id__order_primary_care_provider_post(
    body,
    { params: { kit_id: options.kitId }, signal: options.signal },
  )
}

type SaveEligibilityQuestionnaireOptions = {
  signal?: AbortSignal
  keepalive?: boolean
}

export async function saveEligibilityQuestionnaireResponse(
  kitId: string,
  responses: CreateEligibilityQuestionnaireDTOType['responses'],
  options: SaveEligibilityQuestionnaireOptions = {},
): Promise<EligibilityQuestionnaireResponsesSchemaType> {
  const payload: CreateEligibilityQuestionnaireDTOType = {
    kitId,
    responses,
  }

  if (options.keepalive) {
    return saveEligibilityQuestionnaireResponseKeepalive(kitId, payload, options.signal)
  }

  return portalApiClient.save_eligibility_questionnaire_v2_kits__kit_id__eligibility_questionnaire_post(
    payload,
    { params: { kit_id: kitId }, signal: options.signal },
  )
}

function axiosHeadersToRecord(headers: AxiosHeaders): Record<string, string> {
  // toJSON(true) joins array values with ', ' and omits null/false values.
  return { ...headers.toJSON(true) }
}

function saveEligibilityQuestionnaireResponseKeepalive(
  kitId: string,
  payload: CreateEligibilityQuestionnaireDTOType,
  signal?: AbortSignal,
): Promise<EligibilityQuestionnaireResponsesSchemaType> {
  const config = {
    headers: new AxiosHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  } as InternalAxiosRequestConfig
  // Call fetch in this turn. Waiting for headers first lets the browser close
  // the page before the keepalive POST starts.
  applyPortalApiBrowserRequestHeadersSync(config)

  const baseUrl = getPortalApiBaseUrl().replace(/\/$/, '')
  const responsePromise = fetch(
    `${baseUrl}/v2/kits/${encodeURIComponent(kitId)}/eligibility-questionnaire`,
    {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      signal,
      headers: axiosHeadersToRecord(AxiosHeaders.from(config.headers)),
      body: JSON.stringify(payload),
    },
  )

  return readKeepaliveEligibilityResponse(responsePromise)
}

async function readKeepaliveEligibilityResponse(
  responsePromise: Promise<Response>,
): Promise<EligibilityQuestionnaireResponsesSchemaType> {
  const response = await responsePromise

  if (!response.ok) {
    throw new Error('Unable to save eligibility questionnaire responses.')
  }

  return (await response.json()) as EligibilityQuestionnaireResponsesSchemaType
}

const clearCache = () => {
  kitsCache.clear()
}

const kitsService = {
  getUserKits,
  getKitTrackingUrls,
  registerKit,
  validateKit,
  fetchKitPartnerId,
  fetchKitValidationInfo,
  clearCache,
}

export default kitsService
